import React, { useEffect, useState, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { FiDollarSign, FiSearch, FiRefreshCcw } from 'react-icons/fi'
import { fetchPayments } from '../../store/slices/adminSlice'
import Table from '../../components/Table/Table'
import StatusBadge from '../../components/StatusBadge/StatusBadge'
import Select from '../../components/Select/Select'
import './Payments.css'

const Payments = () => {
  const dispatch = useDispatch()
  const { payments, isLoading, error } = useSelector((state) => state.admin)
  
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [page, setPage] = useState(1)

  useEffect(() => {
    loadPayments()
  }, [page, statusFilter])

  const loadPayments = async () => {
    const params = {
      page,
      limit: 25,
    }
    if (statusFilter !== 'all') {
      params.status = statusFilter
    }
    dispatch(fetchPayments(params))
  }

  const filteredPayments = useMemo(() => {
    const paymentsList = payments?.payments || payments?.data || []
    if (!Array.isArray(paymentsList)) return []
    const query = searchQuery.toLowerCase()
    return paymentsList.filter((payment) => {
      const matchesSearch =
        !query ||
        [
          payment.user?.firstName,
          payment.user?.lastName,
          payment.user?.email,
          payment.transactionId,
        ]
          .filter(Boolean)
          .some((field) => field.toLowerCase().includes(query))
      return matchesSearch
    })
  }, [payments, searchQuery])

  const totalRevenue = useMemo(() => {
    const paymentsList = payments?.payments || payments?.data || []
    if (!Array.isArray(paymentsList)) return 0
    return paymentsList
      .filter((p) => p.status === 'completed' || p.status === 'success')
      .reduce((sum, p) => sum + (p.amount || 0), 0)
  }, [payments])

  const columns = [
    {
      key: 'student',
      label: 'Student',
      render: (row) => (
        <div>
          <strong>
            {row.user?.firstName} {row.user?.lastName}
          </strong>
          <span className="payment-email">{row.user?.email}</span>
        </div>
      ),
    },
    {
      key: 'amount',
      label: 'Amount',
      render: (row) => `₹${row.amount || 0}`,
    },
    {
      key: 'method',
      label: 'Payment Method',
      render: (row) => row.paymentMethod || '—',
    },
    {
      key: 'status',
      label: 'Status',
      render: (row) => <StatusBadge status={row.status || 'pending'} />,
    },
    {
      key: 'date',
      label: 'Date',
      render: (row) => {
        if (!row.createdAt) return '—'
        return new Date(row.createdAt).toLocaleDateString()
      },
    },
    {
      key: 'transactionId',
      label: 'Transaction ID',
      render: (row) => (
        <span className="transaction-id">{row.transactionId || '—'}</span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (row) => (
        <Link to={`/payments/${row._id}`} className="action-link">
          View Details
        </Link>
      ),
    },
  ]

  return (
    <div className="payments-page">
      <header className="page-header">
        <div>
          <p className="eyebrow">Payment Management</p>
          <h1>All Payments</h1>
          <p className="subhead">View and manage payment transactions</p>
        </div>
        <div className="header-actions">
          <div className="revenue-card">
            <FiDollarSign size={20} />
            <div>
              <span>Total Revenue</span>
              <strong>₹{totalRevenue.toLocaleString()}</strong>
            </div>
          </div>
          <button
            type="button"
            className="refresh-btn"
            onClick={loadPayments}
            disabled={isLoading}
          >
            <FiRefreshCcw size={16} />
            Refresh
          </button>
        </div>
      </header>

      <section className="toolbar">
        <div className="search-field">
          <FiSearch size={18} />
          <input
            type="search"
            placeholder="Search by student or transaction ID"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <Select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value)
            setPage(1)
          }}
          options={[
            { value: 'all', label: 'All Status' },
            { value: 'success', label: 'Success' },
            { value: 'pending', label: 'Pending' },
            { value: 'failed', label: 'Failed' },
          ]}
        />
      </section>

      {error && (
        <div className="error-message" role="alert">
          {error}
        </div>
      )}

      <Table
        columns={columns}
        data={filteredPayments}
        loading={isLoading}
        emptyMessage="No payments found"
      />
    </div>
  )
}

export default Payments

